import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Trash2, Receipt, Search, X, Loader2, Check, History,
  ChevronRight, Pencil, AlertTriangle, ArrowUpRight, ArrowDownRight, Minus,
  Package,
} from "lucide-react";
import { api, formatApiError } from "../../lib/api";

/**
 * AdminStockInvoices — /admin/faturas-stock
 *
 * Renamed on the UI to "Stock de produtos" (inventory log).
 *
 * Per the final product decision, this page tracks ONLY quantities — no
 * prices, VAT or totals are shown. Each line quantity is fully editable
 * inline and PATCHing a line automatically adjusts the linked product's
 * stock_quantity on the server (see stock_invoices.py).
 *
 * URL path kept as /admin/faturas-stock so deep-links / bookmarks keep
 * working even though the label changed.
 */
function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("pt-PT", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function sumQty(lines) {
  return (lines || []).reduce((s, l) => s + (Number(l.quantity) || 0), 0);
}

export default function AdminStockInvoices() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [alerts, setAlerts] = useState({ items: [], count: 0, critical_count: 0, low_count: 0 });
  const [globalListOpen, setGlobalListOpen] = useState(false);
  const [globalData, setGlobalData] = useState({ produtos: [], materiais: [] });

  // Load the full global inventory once — reused by the clickable KPI at
  // the top and by the Stock Global card at the bottom so we don't hit
  // the endpoint twice.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/ledger/inventory-global");
        setGlobalData({
          produtos: data.produtos || [],
          materiais: data.materiais || [],
        });
      } catch { /* silent */ }
    })();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  async function loadAlerts() {
    try {
      const { data } = await api.get("/admin/stock/alerts");
      setAlerts(data);
    } catch { /* silent — banner just won't show */ }
  }

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/stock/invoices", {
        params: debouncedSearch ? { search: debouncedSearch, limit: 100 } : { limit: 100 },
      });
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [debouncedSearch]);
  useEffect(() => { loadAlerts(); }, []);

  async function deleteInvoice(inv) {
    const totalQty = sumQty(inv.lines);
    if (!window.confirm(`Eliminar a entrada ${inv.number}? Isto reverte o stock (−${totalQty} unidades).`)) return;
    try {
      const { data: r } = await api.delete(`/admin/stock/invoices/${inv.id}`);
      toast.success(`${inv.number} eliminada · ${r.reverted} linha(s) revertidas.`);
      load();
      loadAlerts();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  // Called by child rows after a successful PATCH. Updates one invoice in place
  // so the expanded row re-renders without a full reload flicker.
  function replaceInvoice(next) {
    setItems(rows => rows.map(r => r.id === next.id ? next : r));
    loadAlerts();
  }

  const kpis = useMemo(() => {
    const totalEntries = items.reduce((s, i) => s + sumQty(i.lines), 0);
    const totalProdutosDistintos = globalData.produtos.length + globalData.materiais.length;
    const totalUnidadesStock = globalData.produtos.reduce((s, r) => s + (Number(r.stock_quantity) || 0), 0)
      + globalData.materiais.reduce((s, r) => s + (Number(r.stock_quantity) || 0), 0);
    return {
      count: items.length,
      units: Math.round(totalEntries * 1000) / 1000,
      latest: items[0]?.number || "—",
      total_items: totalProdutosDistintos,
      total_units_stock: Math.round(totalUnidadesStock * 1000) / 1000,
    };
  }, [items, globalData]);

  return (
    <div className="space-y-8 max-w-6xl" data-testid="stock-invoices-page">
      <header className="space-y-4">
        <div className="eyebrow">Gestão de stock</div>
        <h1 className="font-display text-4xl">Stock de produtos</h1>
        <p className="text-sm text-[#a0b2af] max-w-2xl">
          Registo interno de entradas de stock. Cada entrada recebe um ID
          automático <span className="text-[#d4af37] font-mono">FTR-000001</span> e
          incrementa o stock dos produtos correspondentes. A quantidade de cada
          linha é editável — ao gravar, o stock ajusta-se automaticamente.
        </p>

        <div className="flex flex-wrap items-center gap-3 pt-2">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a0b2af]" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Procurar por FTR-NNNNNN ou nome do produto…"
              className="w-full bg-transparent border border-[#1a4640] focus:border-[#d4af37] rounded-sm pl-9 pr-3 py-2 text-sm outline-none"
              data-testid="stock-invoices-search"
            />
            {search && (
              <button onClick={() => setSearch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-[#a0b2af] hover:text-[#fdfbf7]">
                <X size={12} />
              </button>
            )}
          </div>
          <button onClick={() => setCreateOpen(true)}
                  className="btn-gold px-5 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2"
                  data-testid="stock-invoices-create-btn">
            <Plus size={14} /> Nova entrada
          </button>
        </div>
      </header>

      {/* Low-stock alerts banner */}
      {alerts.count > 0 && (
        <AlertsBanner alerts={alerts} />
      )}

      {/* Summary — now strictly quantity-based */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="stock-invoices-kpis">
        <KpiCard label="Entradas visíveis" value={kpis.count} testId="kpi-stock-count" />
        <KpiCard label="Unidades (total)" value={kpis.units} gold testId="kpi-stock-units" />
        <KpiCard label="Itens no inventário"
                 value={kpis.total_items}
                 gold
                 onClick={() => setGlobalListOpen(true)}
                 hint="Clique para ver todos"
                 testId="kpi-stock-total-items" />
        <KpiCard label="Última entrada" value={kpis.latest} mono testId="kpi-stock-latest" />
      </section>

      {globalListOpen && (
        <GlobalStockListModal produtos={globalData.produtos}
                              materiais={globalData.materiais}
                              onClose={() => setGlobalListOpen(false)} />
      )}

      {/* List */}
      <section className="lux-card overflow-hidden" data-testid="stock-invoices-list">
        <div className="overflow-x-auto">
          <div style={{ minWidth: 760 }}>
            <div className="grid grid-cols-[140px_170px_1fr_120px_60px] gap-3 px-4 py-3 border-b border-[#1a4640] bg-[#041f1b] text-[10px] uppercase tracking-widest text-[#a0b2af]">
              <div>FTR</div>
              <div>Data</div>
              <div>Produtos</div>
              <div className="text-right">Qtd total</div>
              <div></div>
            </div>

            {loading ? (
              <div className="px-4 py-10 text-center text-sm text-[#a0b2af]">
                <Loader2 size={18} className="mx-auto animate-spin text-[#d4af37]" />
              </div>
            ) : items.length === 0 ? (
              <div className="px-4 py-16 text-center text-sm text-[#a0b2af]" data-testid="stock-invoices-empty">
                {debouncedSearch
                  ? `Sem resultados para "${debouncedSearch}".`
                  : "Ainda sem entradas. Clica em \"Nova entrada\" para começar."}
              </div>
            ) : (
              <div className="divide-y divide-[#1a4640]">
                {items.map(inv => {
                  const totalQty = sumQty(inv.lines);
                  const isOpen = expandedId === inv.id;
                  return (
                    <div key={inv.id} data-testid={`stock-invoice-row-${inv.id}`}>
                      <div className="grid grid-cols-[140px_170px_1fr_120px_60px] gap-3 px-4 py-3 items-center hover:bg-[#0a3a33]/30 transition-colors cursor-pointer"
                           onClick={() => setExpandedId(isOpen ? null : inv.id)}>
                        <div className="font-mono text-[#d4af37] text-sm font-semibold">
                          {inv.number}
                        </div>
                        <div className="text-xs text-[#a0b2af]">
                          {fmtDate(inv.created_at)}
                        </div>
                        <div className="text-sm text-[#fdfbf7] truncate" title={inv.lines.map(l => l.product_name).join(" · ")}>
                          {inv.lines.slice(0, 3).map(l => l.product_name).join(" · ")}
                          {inv.lines.length > 3 && <span className="text-[#a0b2af]"> +{inv.lines.length - 3} mais</span>}
                        </div>
                        <div className="text-right text-sm tabular-nums font-display text-[#d4af37]">
                          {totalQty}
                        </div>
                        <div className="flex items-center justify-end gap-1">
                          <ChevronRight size={14} className={`text-[#a0b2af] transition-transform ${isOpen ? "rotate-90" : ""}`} />
                          <button onClick={e => { e.stopPropagation(); deleteInvoice(inv); }}
                                  className="p-1.5 text-[#a0b2af] hover:text-[#ef4444] transition-colors"
                                  title="Eliminar entrada (reverte stock)"
                                  data-testid={`stock-invoice-delete-${inv.id}`}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                      {isOpen && (
                        <div className="bg-[#031411] px-4 py-4 border-t border-[#1a4640]/50"
                             data-testid={`stock-invoice-detail-${inv.id}`}>
                          {inv.notes && (
                            <div className="text-xs text-[#a0b2af] mb-3 italic">
                              Notas: {inv.notes}
                            </div>
                          )}
                          <InvoiceLinesTable
                            invoice={inv}
                            onInvoiceUpdated={replaceInvoice}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Global inventory — every product/material with name + stock qty */}
      <InventorySection refreshKey={items.length + "-" + alerts.count} />

      {createOpen && (
        <NewInvoiceModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); load(); loadAlerts(); }}
        />
      )}
    </div>
  );
}

function KpiCard({ label, value, gold, mono, testId, onClick, hint }) {
  const valClass = gold ? "text-[#d4af37]" : mono ? "text-[#fdfbf7] font-mono" : "text-[#fdfbf7]";
  if (onClick) {
    return (
      <button type="button" onClick={onClick}
              className="lux-card p-4 text-left hover:bg-[#d4af37]/5 hover:border-[#d4af37]/60 transition-colors cursor-pointer group"
              data-testid={testId}>
        <div className="flex items-center justify-between gap-2">
          <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">{label}</div>
          <ChevronRight size={14} className="text-[#d4af37] group-hover:translate-x-0.5 transition-transform" />
        </div>
        <div className={`mt-2 font-display text-2xl ${valClass}`}>{value}</div>
        {hint && <div className="text-[10px] text-[#d4af37]/80 mt-1">{hint}</div>}
      </button>
    );
  }
  return (
    <div className="lux-card p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">{label}</div>
      <div className={`mt-2 font-display text-2xl ${valClass}`}>{value}</div>
    </div>
  );
}

/**
 * Expandable banner. Always visible (collapsed) when there's at least one
 * low/critical product. Click to show the full list. Renders an inline
 * severity bullet (red=rutura, amber=baixo) and the unit shortfall hint.
 */
function AlertsBanner({ alerts }) {
  const [open, setOpen] = useState(false);
  const hasCritical = alerts.critical_count > 0;
  return (
    <section
      className={`lux-card p-4 border ${hasCritical ? "border-[#ef4444]/40 bg-[#ef4444]/5" : "border-[#f59e0b]/40 bg-[#f59e0b]/5"}`}
      data-testid="stock-alerts-banner">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-3 text-left"
        data-testid="stock-alerts-toggle">
        <div className="flex items-center gap-3">
          <AlertTriangle size={18} className={hasCritical ? "text-[#ef4444]" : "text-[#f59e0b]"} />
          <div>
            <div className="text-sm font-display text-[#fdfbf7]">
              {alerts.count} produto(s) precisam de reposição
            </div>
            <div className="text-[11px] text-[#a0b2af] mt-0.5">
              {alerts.critical_count > 0 && (
                <span className="text-[#ef4444]">
                  {alerts.critical_count} em rutura
                </span>
              )}
              {alerts.critical_count > 0 && alerts.low_count > 0 && <span> · </span>}
              {alerts.low_count > 0 && (
                <span className="text-[#f59e0b]">
                  {alerts.low_count} abaixo do consumo semanal estimado
                </span>
              )}
            </div>
          </div>
        </div>
        <ChevronRight size={16} className={`text-[#a0b2af] transition-transform ${open ? "rotate-90" : ""}`} />
      </button>

      {open && (
        <ul className="mt-3 pt-3 border-t border-[#1a4640]/60 space-y-1.5"
            data-testid="stock-alerts-list">
          {alerts.items.map(a => (
            <li key={a.id}
                className="flex items-center justify-between gap-3 text-xs"
                data-testid={`stock-alert-${a.id}`}>
              <div className="flex items-center gap-2 min-w-0">
                <span
                  className={`inline-block w-2 h-2 rounded-full shrink-0 ${a.stock_status === "critical" ? "bg-[#ef4444] animate-pulse" : "bg-[#f59e0b]"}`}
                />
                <span className="truncate text-[#fdfbf7]">{a.name}</span>
                {a.code && <span className="font-mono text-[10px] text-[#d4af37] shrink-0">{a.code}</span>}
                <span className="text-[10px] text-[#a0b2af] shrink-0">
                  · {a.category === "material" ? "Material" : "Produto"}
                </span>
              </div>
              <div className="text-[11px] text-[#a0b2af] shrink-0 tabular-nums">
                <span className={a.stock_status === "critical" ? "text-[#ef4444]" : "text-[#fdfbf7]"}>
                  {a.stock_quantity}
                </span>
                <span className="mx-1">/</span>
                <span className="text-[#a0b2af]">{a.weekly_need}</span>
                <span className="ml-1 text-[#a0b2af]/70">{a.unit_label}/sem</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Quantity-only table with inline editing. Each row has a pencil → number
 * input → Check/Cancel. On save we PATCH the line and replace the whole
 * invoice doc in the parent state (the server returns the updated invoice).
 */
function InvoiceLinesTable({ invoice, onInvoiceUpdated }) {
  return (
    <table className="w-full text-sm">
      <thead className="text-[10px] uppercase tracking-widest text-[#a0b2af]">
        <tr>
          <th className="text-left py-1.5 w-28">Código</th>
          <th className="text-left py-1.5">Produto</th>
          <th className="text-left py-1.5 w-24">Tipo</th>
          <th className="text-right py-1.5 w-40">Quantidade</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-[#1a4640]/40">
        {invoice.lines.map(ln => (
          <EditableLineRow
            key={ln.line_id}
            invoiceId={invoice.id}
            line={ln}
            onInvoiceUpdated={onInvoiceUpdated}
          />
        ))}
      </tbody>
      <tfoot>
        <tr className="border-t border-[#1a4640]">
          <td colSpan={3} className="py-2 text-right text-xs uppercase tracking-widest text-[#a0b2af]">
            Total de unidades nesta entrada
          </td>
          <td className="py-2 text-right font-display text-lg text-[#d4af37] tabular-nums">
            {sumQty(invoice.lines)}
          </td>
        </tr>
      </tfoot>
    </table>
  );
}

function EditableLineRow({ invoiceId, line, onInvoiceUpdated }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(line.quantity));
  const [saving, setSaving] = useState(false);

  function startEdit() {
    setValue(String(line.quantity));
    setEditing(true);
  }

  async function save() {
    const qty = Number(value);
    if (!isFinite(qty) || qty <= 0) {
      toast.error("Quantidade deve ser maior que zero.");
      return;
    }
    if (qty === Number(line.quantity)) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(
        `/admin/stock/invoices/${invoiceId}/lines/${line.line_id}`,
        { quantity: qty },
      );
      onInvoiceUpdated(data);
      toast.success(`Quantidade atualizada · ${line.product_name}: ${line.quantity} → ${qty}`);
      setEditing(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  }

  return (
    <tr data-testid={`stock-line-${line.line_id}`}>
      <td className="py-2 font-mono text-[#d4af37] text-xs">{line.product_code || "—"}</td>
      <td className="py-2">{line.product_name}</td>
      <td className="py-2 text-xs text-[#a0b2af]">
        {line.category === "material" ? "Material" : "Produto"}
      </td>
      <td className="py-2 text-right">
        {editing ? (
          <div className="flex items-center justify-end gap-1">
            <input
              type="number" step="0.01" min="0.01"
              autoFocus
              value={value}
              onChange={e => setValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") save();
                if (e.key === "Escape") setEditing(false);
              }}
              className="w-24 bg-transparent border border-[#d4af37] rounded-sm px-2 py-1 text-sm text-right outline-none"
              data-testid={`stock-line-qty-input-${line.line_id}`}
            />
            <button onClick={save} disabled={saving}
                    className="p-1 text-[#10b981] hover:bg-[#10b981]/10 rounded-sm disabled:opacity-50"
                    title="Guardar"
                    data-testid={`stock-line-save-${line.line_id}`}>
              {saving
                ? <Loader2 size={14} className="animate-spin" />
                : <Check size={14} />}
            </button>
            <button onClick={() => setEditing(false)} disabled={saving}
                    className="p-1 text-[#a0b2af] hover:text-[#ef4444] rounded-sm disabled:opacity-50"
                    title="Cancelar"
                    data-testid={`stock-line-cancel-${line.line_id}`}>
              <X size={14} />
            </button>
          </div>
        ) : (
          <button onClick={startEdit}
                  className="inline-flex items-center gap-1.5 tabular-nums hover:text-[#d4af37] transition-colors group"
                  title="Clique para editar a quantidade"
                  data-testid={`stock-line-qty-${line.line_id}`}>
            <span>{line.quantity}</span>
            <Pencil size={11} className="text-[#a0b2af] group-hover:text-[#d4af37]" />
          </button>
        )}
      </td>
    </tr>
  );
}

function NewInvoiceModal({ onClose, onCreated }) {
  const [products, setProducts] = useState([]);  // existing products for autocomplete
  const [lines, setLines] = useState([newLine()]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const mk = new Date();
        const month = `${mk.getFullYear()}-${String(mk.getMonth() + 1).padStart(2, "0")}`;
        const { data } = await api.get("/admin/ledger", { params: { month } });
        const all = [...(data.produtos || []), ...(data.materiais || [])];
        setProducts(all);
      } catch { /* fail-open */ }
    })();
  }, []);

  function newLine() {
    return {
      id: `tmp-${Date.now()}-${Math.random()}`,
      product_id: null, name: "", code: "",
      category: "produto", quantity: 1,
    };
  }

  function patch(id, p) {
    setLines(rows => rows.map(r => r.id === id ? { ...r, ...p } : r));
  }

  function remove(id) {
    setLines(rows => rows.length <= 1 ? rows : rows.filter(r => r.id !== id));
  }

  function pickProduct(id, prod) {
    patch(id, {
      product_id: prod.id, name: prod.name,
      code: prod.code || "", category: prod.category,
    });
  }

  const totalUnits = useMemo(
    () => lines.reduce((s, l) => s + (Number(l.quantity) || 0), 0),
    [lines],
  );

  async function submit() {
    const clean = lines.filter(l => l.name.trim() && Number(l.quantity) > 0);
    if (!clean.length) {
      toast.error("Adicione pelo menos uma linha válida (nome + quantidade > 0).");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        // Send without prices — backend defaults unit_price=0 on the Pydantic model.
        lines: clean.map(l => ({
          product_id: l.product_id || undefined,
          name: l.name.trim(),
          code: (l.code || "").trim() || undefined,
          category: l.category,
          quantity: Number(l.quantity),
        })),
        notes: notes.trim() || undefined,
      };
      const { data } = await api.post("/admin/stock/invoices", payload);
      toast.success(`${data.number} criada · ${data.lines.length} linha(s) · ${sumQty(data.lines)} unidade(s)`);
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose}
         data-testid="new-stock-invoice-modal">
      <div className="lux-card w-full max-w-4xl max-h-[92vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <header className="flex items-center justify-between p-5 border-b border-[#1a4640] sticky top-0 bg-[#031c18] z-10">
          <div>
            <div className="eyebrow text-[#d4af37] mb-1 flex items-center gap-2">
              <Receipt size={12} /> Nova entrada de stock
            </div>
            <div className="font-display text-xl">Registar entrada de produtos</div>
          </div>
          <button onClick={onClose}
                  className="p-2 text-[#a0b2af] hover:text-[#fdfbf7]"
                  data-testid="new-stock-invoice-close">
            <X size={18} />
          </button>
        </header>

        <div className="p-5 space-y-4">
          <div className="text-xs text-[#a0b2af]">
            O ID <span className="text-[#d4af37] font-mono">FTR-NNNNNN</span> é
            atribuído automaticamente. Para cada linha, escolha um produto
            existente (autocomplete) ou digite um novo nome — o sistema cria-o
            em automático. Só a <span className="text-[#d4af37]">quantidade</span> é
            necessária.
          </div>

          <div className="space-y-3">
            {lines.map((l, idx) => (
              <LineRow key={l.id}
                       line={l} index={idx + 1}
                       products={products}
                       onChange={p => patch(l.id, p)}
                       onPick={prod => pickProduct(l.id, prod)}
                       onRemove={() => remove(l.id)}
                       canRemove={lines.length > 1} />
            ))}
            <button onClick={() => setLines(rs => [...rs, newLine()])}
                    className="btn-ghost-gold w-full py-2 text-xs uppercase tracking-widest inline-flex items-center justify-center gap-2"
                    data-testid="new-stock-invoice-add-line">
              <Plus size={14} /> Adicionar produto
            </button>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Notas (opcional)</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
                      placeholder="Ex.: Compra mensal fornecedor X"
                      className="w-full bg-transparent border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-3 py-2 text-sm outline-none mt-1"
                      rows={2}
                      data-testid="new-stock-invoice-notes" />
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-[#1a4640]">
            <div className="text-sm">
              <span className="text-[#a0b2af]">Total unidades </span>
              <span className="font-display text-xl text-[#d4af37] tabular-nums" data-testid="new-stock-invoice-total">
                {totalUnits}
              </span>
            </div>
            <button onClick={submit} disabled={saving}
                    className="btn-gold px-6 py-3 text-sm flex items-center gap-2 disabled:opacity-50"
                    data-testid="new-stock-invoice-submit">
              {saving
                ? <><Loader2 size={14} className="animate-spin" /> A gravar…</>
                : <><Check size={14} /> Guardar entrada</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function LineRow({ line, index, products, onChange, onPick, onRemove, canRemove }) {
  const [open, setOpen] = useState(false);
  const matches = useMemo(() => {
    const q = (line.name || "").toLowerCase().trim();
    if (!q) return [];
    return products.filter(p =>
      p.name.toLowerCase().includes(q)
      || (p.code || "").toLowerCase().includes(q)
    ).slice(0, 6);
  }, [line.name, products]);
  const picked = !!line.product_id;

  return (
    <div className="lux-card p-3 space-y-2"
         data-testid={`new-stock-invoice-line-${index}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">
          Linha {index} {picked && <span className="text-[#10b981]">· Produto existente</span>}
        </div>
        {canRemove && (
          <button onClick={onRemove}
                  className="p-1 text-[#a0b2af] hover:text-[#ef4444]"
                  data-testid={`new-stock-invoice-remove-${index}`}>
            <Trash2 size={12} />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_110px_110px_110px] gap-2">
        {/* Name with autocomplete */}
        <div className="relative">
          <input
            value={line.name}
            onChange={e => { onChange({ name: e.target.value, product_id: null }); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            placeholder="Nome do produto (novo ou existente)"
            className={`w-full bg-transparent border rounded-sm px-2 py-1.5 text-sm outline-none ${picked ? "border-[#10b981]/60" : "border-[#1a4640] focus:border-[#d4af37]"}`}
            data-testid={`new-stock-invoice-name-${index}`}
          />
          {open && matches.length > 0 && (
            <div className="absolute z-10 mt-1 left-0 right-0 bg-[#041f1b] border border-[#1a4640] rounded-sm max-h-48 overflow-y-auto shadow-lg">
              {matches.map(p => (
                <button key={p.id}
                        onClick={() => { onPick(p); setOpen(false); }}
                        className="w-full text-left px-2 py-1.5 text-xs hover:bg-[#0a3a33] flex items-center justify-between gap-2"
                        data-testid={`new-stock-invoice-suggest-${p.id}`}>
                  <span className="truncate">{p.name}</span>
                  <span className="text-[10px] text-[#a0b2af] shrink-0">
                    {p.code && <span className="font-mono text-[#d4af37] mr-1">{p.code}</span>}
                    stock {p.stock_quantity || 0}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Code */}
        <input
          value={line.code}
          onChange={e => onChange({ code: e.target.value })}
          placeholder="Código"
          className="bg-transparent border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-2 py-1.5 text-xs font-mono text-[#d4af37] outline-none"
          data-testid={`new-stock-invoice-code-${index}`}
        />

        {/* Category */}
        <select value={line.category}
                onChange={e => onChange({ category: e.target.value, product_id: null })}
                className="bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-2 py-1.5 text-xs outline-none"
                data-testid={`new-stock-invoice-category-${index}`}>
          <option value="produto">Produto</option>
          <option value="material">Material</option>
        </select>

        {/* Quantity (only numeric field persisted) */}
        <input type="number" step="0.01" min="0.01"
               value={line.quantity}
               onChange={e => onChange({ quantity: e.target.value })}
               className="bg-transparent border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-2 py-1.5 text-sm text-right outline-none"
               placeholder="Qtd"
               data-testid={`new-stock-invoice-qty-${index}`} />
      </div>
    </div>
  );
}

/**
 * Small side modal — used from AdminExpenses to show the history of FTR
 * entries that include a given product. Clicking an entry opens the full
 * detail with editable quantities.
 */
export function ProductInvoicesHistoryModal({ productId, productName, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [openInvoice, setOpenInvoice] = useState(null);  // full-detail modal

  async function reload() {
    try {
      const { data } = await api.get(`/admin/stock/products/${productId}/invoices`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [productId]);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose}
         data-testid="product-invoices-modal">
      <div className="lux-card w-full max-w-2xl max-h-[80vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <header className="flex items-center justify-between p-4 border-b border-[#1a4640] sticky top-0 bg-[#031c18] z-10">
          <div>
            <div className="eyebrow text-[#d4af37] mb-1 flex items-center gap-2">
              <History size={12} /> Histórico de entradas
            </div>
            <div className="font-display text-lg">{productName}</div>
          </div>
          <button onClick={onClose} className="p-2 text-[#a0b2af] hover:text-[#fdfbf7]"
                  data-testid="product-invoices-close">
            <X size={16} />
          </button>
        </header>
        <div className="p-4">
          {loading ? (
            <div className="text-center py-6"><Loader2 size={18} className="mx-auto animate-spin text-[#d4af37]" /></div>
          ) : !data || data.items.length === 0 ? (
            <div className="text-center py-8 text-sm text-[#a0b2af]"
                 data-testid="product-invoices-empty">
              Ainda sem entradas registadas para este produto.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-[#a0b2af] border-b border-[#1a4640]">
                <tr>
                  <th className="text-left py-2">FTR</th>
                  <th className="text-left py-2">Data</th>
                  <th className="text-right py-2">Qtd</th>
                  <th className="w-6"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1a4640]/40">
                {data.items.map(i => (
                  <tr key={i.invoice_id}
                      className="hover:bg-[#0a3a33]/40 cursor-pointer transition-colors"
                      onClick={() => setOpenInvoice(i.invoice_id)}
                      title="Clique para ver a entrada completa"
                      data-testid={`product-invoice-row-${i.invoice_id}`}>
                    <td className="py-2 font-mono text-[#d4af37] text-xs">{i.number}</td>
                    <td className="py-2 text-xs text-[#a0b2af]">{fmtDate(i.created_at)}</td>
                    <td className="py-2 text-right tabular-nums">{i.quantity}</td>
                    <td className="py-2 text-right"><ChevronRight size={12} className="text-[#a0b2af]" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {openInvoice && (
        <InvoiceDetailModal
          invoiceId={openInvoice}
          onClose={() => setOpenInvoice(null)}
          onUpdated={reload}
        />
      )}
    </div>
  );
}

/**
 * Full entry detail — shows ALL lines of an FTR with editable quantities.
 * After an edit the parent (`ProductInvoicesHistoryModal`) re-fetches so
 * the history list picks up the new quantity.
 */
export function InvoiceDetailModal({ invoiceId, onClose, onUpdated }) {
  const [loading, setLoading] = useState(true);
  const [inv, setInv] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/admin/stock/invoices/${invoiceId}`);
        setInv(data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [invoiceId]);

  function handleUpdated(next) {
    setInv(next);
    onUpdated && onUpdated();
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose}
         data-testid="invoice-detail-modal">
      <div className="lux-card w-full max-w-3xl max-h-[85vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <header className="flex items-center justify-between p-4 border-b border-[#1a4640] sticky top-0 bg-[#031c18] z-10">
          <div>
            <div className="eyebrow text-[#d4af37] mb-1 flex items-center gap-2">
              <Receipt size={12} /> Entrada de stock
            </div>
            <div className="font-display text-xl font-mono">{inv?.number || "…"}</div>
            {inv && <div className="text-xs text-[#a0b2af] mt-1">{fmtDate(inv.created_at)}</div>}
          </div>
          <button onClick={onClose} className="p-2 text-[#a0b2af] hover:text-[#fdfbf7]"
                  data-testid="invoice-detail-close">
            <X size={16} />
          </button>
        </header>
        <div className="p-4">
          {loading || !inv ? (
            <div className="text-center py-10"><Loader2 size={18} className="mx-auto animate-spin text-[#d4af37]" /></div>
          ) : (
            <>
              {inv.notes && (
                <div className="text-xs text-[#a0b2af] italic mb-3 border-l-2 border-[#d4af37]/40 pl-3">
                  {inv.notes}
                </div>
              )}
              <InvoiceLinesTable invoice={inv} onInvoiceUpdated={handleUpdated} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}


/**
 * Global inventory view — a read-only snapshot of every product + material
 * in the company with name, optional code, and current stock. Lives BELOW
 * the FTR list at the admin's explicit request (see session messages).
 *
 * Data source: /admin/ledger (current month) — the same endpoint that
 * powers /admin/produtos & /admin/materiais, already enriched with
 * stock_status/weekly_need/low_stock so we can reuse the severity dots.
 */
function InventorySection({ refreshKey }) {
  const [loading, setLoading] = useState(true);
  const [produtos, setProdutos] = useState([]);
  const [materiais, setMateriais] = useState([]);
  const [filter, setFilter] = useState("");
  const [pickedId, setPickedId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/ledger/inventory-global");
        setProdutos(data.produtos || []);
        setMateriais(data.materiais || []);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [refreshKey]);

  const q = filter.trim().toLowerCase();
  const filt = rows => !q
    ? rows
    : rows.filter(r =>
        r.name.toLowerCase().includes(q)
        || (r.code || "").toLowerCase().includes(q));

  const fp = filt(produtos);
  const fm = filt(materiais);

  return (
    <section className="space-y-4" data-testid="stock-inventory-section">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="eyebrow">Inventário global</div>
          <h2 className="font-display text-2xl mt-1">Stock atual</h2>
          <p className="text-xs text-[#a0b2af] mt-1">
            Todos os produtos e materiais da empresa com a respetiva
            quantidade em stock. Apenas leitura — para editar, use as páginas
            <span className="text-[#d4af37]"> Produtos</span> ou
            <span className="text-[#d4af37]"> Materiais</span>.
          </p>
        </div>
        <div className="relative min-w-[220px] max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a0b2af]" />
          <input
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Filtrar por nome ou código…"
            className="w-full bg-transparent border border-[#1a4640] focus:border-[#d4af37] rounded-sm pl-9 pr-3 py-2 text-sm outline-none"
            data-testid="stock-inventory-filter"
          />
        </div>
      </div>

      {loading ? (
        <div className="lux-card p-8 text-center">
          <Loader2 size={18} className="mx-auto animate-spin text-[#d4af37]" />
        </div>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2">
            <InventoryColumn
              title="Produtos"
              testPrefix="produtos"
              items={fp}
              total={produtos.length}
              empty={q ? `Sem produtos para "${filter}".` : "Sem produtos registados."}
              onPick={(r) => setPickedId(r.id)}
            />
            <InventoryColumn
              title="Materiais de limpeza"
              testPrefix="materiais"
              items={fm}
              total={materiais.length}
              empty={q ? `Sem materiais para "${filter}".` : "Sem materiais registados."}
              onPick={(r) => setPickedId(r.id)}
            />
          </div>
          <GlobalStockSummary produtos={produtos} materiais={materiais} />
        </>
      )}
      {pickedId && (
        <StockItemDetailModal pid={pickedId} onClose={() => setPickedId(null)} />
      )}
    </section>
  );
}

function GlobalStockSummary({ produtos, materiais }) {
  const [trend, setTrend] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    api.get("/admin/stock/global-trend")
      .then(({ data }) => setTrend(data))
      .catch(() => {});
  }, []);
  const totP = useMemo(
    () => produtos.reduce((s, r) => s + (Number(r.stock_quantity) || 0), 0),
    [produtos],
  );
  const totM = useMemo(
    () => materiais.reduce((s, r) => s + (Number(r.stock_quantity) || 0), 0),
    [materiais],
  );
  const round3 = (n) => Math.round(n * 1000) / 1000;
  return (
    <>
      <button type="button"
              onClick={() => setOpen(true)}
              className="w-full text-left lux-card border-[#d4af37]/50 bg-[#d4af37]/5 p-5 hover:bg-[#d4af37]/10 hover:border-[#d4af37] transition-colors cursor-pointer group"
              data-testid="stock-global-summary">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="eyebrow text-[#d4af37]">Stock global da empresa</div>
            <p className="text-[11px] text-[#a0b2af] mt-1">
              Total acumulado, sem filtro de mês. Clique para ver a lista completa.
            </p>
          </div>
          <ChevronRight size={18} className="text-[#d4af37] shrink-0 mt-1 group-hover:translate-x-0.5 transition-transform" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          <Stat label="Produtos distintos" value={produtos.length} testid="global-produtos-distintos" />
          <Stat label="Unidades de produtos" value={round3(totP)} testid="global-produtos-unidades" />
          <Stat label="Materiais distintos" value={materiais.length} testid="global-materiais-distintos" />
          <Stat label="Unidades de materiais" value={round3(totM)} testid="global-materiais-unidades" />
        </div>
        <div className="mt-4 pt-3 border-t border-[#1a4640] flex items-baseline justify-between">
          <div className="text-xs uppercase tracking-widest text-[#a0b2af]">Total de itens (produtos + materiais)</div>
          <div className="font-display text-3xl text-[#d4af37] tabular-nums"
               data-testid="global-total-items">
            {produtos.length + materiais.length}
          </div>
        </div>
        <div className="flex items-baseline justify-between mt-1">
          <div className="text-xs uppercase tracking-widest text-[#a0b2af]">Total de unidades</div>
          <div className="flex items-baseline gap-3">
            <div className="font-display text-3xl text-[#d4af37] tabular-nums"
                 data-testid="global-total-units">
              {round3(totP + totM)}
            </div>
            <TrendBadge trend={trend} />
          </div>
        </div>
        {trend && (trend.now.total !== 0 || trend.past.total !== 0) && (
          <div className="text-[10px] text-[#a0b2af] mt-2"
               data-testid="global-trend-caption">
            {trend.delta.total >= 0 ? "+" : ""}{round3(trend.delta.total)} unid. vs. há 30 dias
            {" "}(entradas–saídas registadas em stock_movements).
          </div>
        )}
      </button>
      {open && (
        <GlobalStockListModal produtos={produtos} materiais={materiais}
                              onClose={() => setOpen(false)} />
      )}
    </>
  );
}

function GlobalStockListModal({ produtos, materiais, onClose }) {
  const round3 = (n) => Math.round(n * 1000) / 1000;
  const totP = produtos.reduce((s, r) => s + (Number(r.stock_quantity) || 0), 0);
  const totM = materiais.reduce((s, r) => s + (Number(r.stock_quantity) || 0), 0);
  const sortedP = [...produtos].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  const sortedM = [...materiais].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/70" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-4 pointer-events-none">
        <div className="bg-[#041f1b] border border-[#d4af37]/40 w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-4xl overflow-hidden flex flex-col pointer-events-auto"
             data-testid="global-stock-list-modal">
          <header className="px-5 py-4 border-b border-[#1a4640] flex items-start justify-between gap-3 sticky top-0 bg-[#041f1b] z-10">
            <div>
              <div className="eyebrow text-[#d4af37]">Stock global da empresa</div>
              <h2 className="font-display text-2xl mt-1 text-[#fdfbf7]">Inventário completo</h2>
              <p className="text-xs text-[#a0b2af] mt-1">
                Todos os produtos e materiais registados, sem exceções.
              </p>
            </div>
            <button onClick={onClose}
                    className="p-2 hover:bg-[#0a3a33] rounded-sm transition-colors"
                    data-testid="global-stock-list-close"
                    aria-label="Fechar">
              <X size={18} className="text-[#a0b2af]" />
            </button>
          </header>

          <div className="flex-1 overflow-y-auto">
            <ListBlock title="Produtos"
                       subtitle={`${produtos.length} distintos · ${round3(totP)} unid.`}
                       items={sortedP}
                       empty="Sem produtos registados."
                       testPrefix="global-list-produto" />
            <ListBlock title="Materiais de limpeza"
                       subtitle={`${materiais.length} distintos · ${round3(totM)} unid.`}
                       items={sortedM}
                       empty="Sem materiais registados."
                       testPrefix="global-list-material" />
          </div>

          <footer className="px-5 py-3 border-t border-[#1a4640] flex items-center justify-between text-xs text-[#a0b2af] bg-[#062c27]">
            <span>Total: <strong className="text-[#d4af37]">{produtos.length + materiais.length}</strong> itens · <strong className="text-[#d4af37]">{round3(totP + totM)}</strong> unid.</span>
            <button onClick={onClose} className="text-[#d4af37] hover:underline uppercase tracking-widest">
              Fechar
            </button>
          </footer>
        </div>
      </div>
    </>
  );
}

function ListBlock({ title, subtitle, items, empty, testPrefix }) {
  return (
    <>
      <div className="sticky top-0 z-20 bg-[#0a3a33] px-5 py-2.5 border-b border-[#1a4640] flex items-baseline justify-between shadow-[0_1px_0_rgba(212,175,55,0.2)]"
           data-testid={`${testPrefix}-header`}>
        <div className="text-xs uppercase tracking-widest text-[#d4af37] font-semibold">{title}</div>
        <div className="text-[10px] text-[#a0b2af]">{subtitle}</div>
      </div>
      {items.length === 0 ? (
        <div className="px-5 py-8 text-center text-xs text-[#a0b2af] border-b border-[#1a4640]">{empty}</div>
      ) : (
        <ul className="divide-y divide-[#1a4640]/60 border-b border-[#1a4640]">
          {items.map(r => {
            const qty = Number(r.stock_quantity) || 0;
            const unit = r.unit_label || "un";
            const status = r.stock_status || "na";
            const dot = status === "critical" ? "bg-[#ef4444] animate-pulse"
                      : status === "low" ? "bg-[#f59e0b]"
                      : status === "ok" ? "bg-[#10b981]"
                      : "bg-[#a0b2af]/40";
            return (
              <li key={r.id}
                  className="px-5 py-3 flex items-center gap-3 bg-[#041f1b]"
                  data-testid={`${testPrefix}-${r.id}`}>
                <span className={`w-2 h-2 rounded-full shrink-0 ${dot}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#fdfbf7] truncate">{r.name}</div>
                  {r.code && (
                    <div className="text-[10px] uppercase tracking-widest text-[#a0b2af] font-mono">
                      {r.code}
                    </div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display text-base text-[#d4af37] tabular-nums">
                    {Math.round(qty * 1000) / 1000}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">{unit}</div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

function TrendBadge({ trend }) {
  if (!trend) return null;
  const pct = trend.pct_change;
  const delta = trend.delta?.total || 0;
  // No past data and nothing now → skip
  if (trend.now.total === 0 && trend.past.total === 0) return null;
  // No pct available but we have a delta: show absolute
  const up = delta > 0;
  const flat = Math.abs(delta) < 0.001;
  const cls = flat
    ? "text-[#a0b2af] border-[#a0b2af]/40"
    : up
      ? "text-[#10b981] border-[#10b981]/50 bg-[#10b981]/10"
      : "text-[#fca5a5] border-[#ef4444]/50 bg-[#ef4444]/10";
  const ArrowIcon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm border text-[11px] font-mono ${cls}`}
          data-testid="global-trend-badge"
          title={`Há 30 dias: ${trend.past.total} unid. · hoje: ${trend.now.total} unid.`}>
      <ArrowIcon size={12} />
      {pct === null || pct === undefined
        ? (delta >= 0 ? "+" : "") + Math.round(delta * 10) / 10
        : `${pct >= 0 ? "+" : ""}${pct}%`}
    </span>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="bg-[#041f1b] border border-[#1a4640] rounded-sm p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">{label}</div>
      <div className="font-display text-2xl text-[#fdfbf7] mt-1 tabular-nums">{value}</div>
    </div>
  );
}

function InventoryColumn({ title, items, total, empty, testPrefix, onPick }) {
  const totalUnits = useMemo(
    () => items.reduce((s, r) => s + (Number(r.stock_quantity) || 0), 0),
    [items],
  );
  return (
    <div className="lux-card overflow-hidden" data-testid={`stock-inventory-${testPrefix}`}>
      <header className="flex items-center justify-between px-4 py-3 border-b border-[#1a4640] bg-[#041f1b]">
        <div className="text-xs uppercase tracking-widest text-[#a0b2af]">
          {title}
          <span className="ml-2 text-[#d4af37] font-mono">
            {items.length}{items.length !== total ? `/${total}` : ""}
          </span>
        </div>
        <div className="text-[11px] text-[#a0b2af]">
          Total unid. <span className="text-[#d4af37] font-display text-base tabular-nums">
            {Math.round(totalUnits * 1000) / 1000}
          </span>
        </div>
      </header>
      {items.length === 0 ? (
        <div className="px-4 py-10 text-center text-xs text-[#a0b2af]"
             data-testid={`stock-inventory-${testPrefix}-empty`}>
          {empty}
        </div>
      ) : (
        <ul className="divide-y divide-[#1a4640]/60 max-h-[420px] overflow-y-auto">
          {items.map(r => (
            <InventoryRow key={r.id} row={r} testPrefix={testPrefix} onPick={onPick} />
          ))}
        </ul>
      )}
    </div>
  );
}

function InventoryRow({ row, testPrefix, onPick }) {
  const qty = Number(row.stock_quantity) || 0;
  const status = row.stock_status || "na";
  const dot = status === "critical"
    ? "bg-[#ef4444] animate-pulse"
    : status === "low"
      ? "bg-[#f59e0b]"
      : qty > 0
        ? "bg-[#10b981]"
        : "bg-[#1a4640]";
  const qtyColor = status === "critical"
    ? "text-[#ef4444]"
    : qty === 0
      ? "text-[#a0b2af]"
      : "text-[#fdfbf7]";
  return (
    <li>
      <button
        type="button"
        onClick={() => onPick && onPick(row)}
        className="w-full flex items-center justify-between gap-3 px-4 py-2 hover:bg-[#0a3a33]/40 active:bg-[#0a3a33]/60 transition-colors text-left"
        data-testid={`stock-inventory-row-${testPrefix}-${row.id}`}
        title="Ver detalhes deste item"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${dot}`}
                title={
                  status === "critical" ? "Rutura"
                    : status === "low" ? "Stock baixo"
                      : qty > 0 ? "OK" : "Sem stock"
                } />
          <span className="text-sm text-[#fdfbf7] truncate">{row.name}</span>
          {row.code && (
            <span className="font-mono text-[10px] text-[#d4af37] shrink-0">
              {row.code}
            </span>
          )}
        </div>
        <div className="text-right shrink-0 flex items-center gap-2">
          <span className={`font-display text-base tabular-nums ${qtyColor}`}
                data-testid={`stock-inventory-qty-${row.id}`}>
            {qty}
          </span>
          <span className="text-[10px] text-[#a0b2af]">
            {row.unit_label || "un"}
          </span>
          <ChevronRight size={14} className="text-[#a0b2af] shrink-0" />
        </div>
      </button>
    </li>
  );
}

function StockItemDetailModal({ pid, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/admin/ledger/inventory-global/${pid}`)
      .then(r => { if (alive) setData(r.data); })
      .catch(e => { toast.error(formatApiError(e)); onClose(); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [pid, onClose]);

  const item = data?.item || {};
  const movements = data?.movements || [];
  const totals = data?.totals || {};

  const status = item.stock_status || "na";
  const statusLabel =
    status === "critical" ? "RUTURA"
    : status === "low" ? "STOCK BAIXO"
    : (Number(item.stock_quantity) || 0) > 0 ? "OK"
    : "SEM STOCK";
  const statusClass =
    status === "critical" ? "bg-[#ef4444]/20 text-[#ef4444] border-[#ef4444]/50"
    : status === "low"    ? "bg-[#f59e0b]/20 text-[#f59e0b] border-[#f59e0b]/50"
    : (Number(item.stock_quantity) || 0) > 0
        ? "bg-[#10b981]/20 text-[#10b981] border-[#10b981]/50"
        : "bg-[#1a4640] text-[#a0b2af] border-[#1a4640]";

  const kindLabel = {
    invoice:           "Entrada (fatura)",
    invoice_reversal:  "Estorno de fatura",
    consumption:       "Consumo (limpeza)",
    adjust:            "Ajuste manual",
    out:               "Saída",
    in:                "Entrada",
  };
  const kindIcon = (k) =>
    k === "invoice" || k === "in"
      ? "↑"
      : k === "consumption" || k === "out"
        ? "↓"
        : "•";
  const kindColor = (k) =>
    k === "invoice" || k === "in" ? "text-[#10b981]"
    : k === "consumption" || k === "out" ? "text-[#ef4444]"
    : "text-[#d4af37]";

  return (
    <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
         onClick={onClose}
         data-testid="stock-item-detail-modal">
      <div className="lux-card w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-sm"
           onClick={e => e.stopPropagation()}>
        <header className="sticky top-0 bg-[#031c18] z-10 px-5 py-4 border-b border-[#1a4640] flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="eyebrow text-[#d4af37] flex items-center gap-2">
              <Package size={11} />
              {item.category === "produto" ? "Produto" : item.category === "material" ? "Material de limpeza" : "Item"}
            </div>
            <div className="font-display text-xl text-[#fdfbf7] mt-1 break-words">
              {item.name || "—"}
            </div>
            {item.code && (
              <div className="font-mono text-xs text-[#d4af37] mt-0.5">{item.code}</div>
            )}
          </div>
          <button onClick={onClose} className="p-2 text-[#a0b2af] hover:text-[#fdfbf7]"
                  data-testid="stock-item-detail-close">
            <X size={16} />
          </button>
        </header>

        {loading || !data ? (
          <div className="text-center py-14">
            <Loader2 size={20} className="mx-auto animate-spin text-[#d4af37]" />
          </div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Hero stock + status */}
            <div className="flex flex-wrap items-end justify-between gap-4 p-4 rounded-sm border border-[#d4af37]/40 bg-[#d4af37]/5">
              <div>
                <div className="eyebrow text-[#a0b2af]">Stock atual</div>
                <div className="font-display text-4xl text-[#d4af37] tabular-nums mt-1">
                  {Math.round((Number(item.stock_quantity) || 0) * 1000) / 1000}
                  <span className="text-base text-[#a0b2af] ml-2">{item.unit_label || "un"}</span>
                </div>
                {Number(item.weekly_need) > 0 && (
                  <div className="text-[11px] text-[#a0b2af] mt-1">
                    Consumo semanal estimado:{" "}
                    <span className="text-[#fdfbf7]">{Math.round(Number(item.weekly_need) * 100) / 100} {item.unit_label || "un"}</span>
                  </div>
                )}
              </div>
              <span className={`text-[10px] uppercase tracking-widest border rounded-full px-2.5 py-1 ${statusClass}`}
                    data-testid="stock-item-status-badge">
                {statusLabel}
              </span>
            </div>

            {/* Pricing + pack info */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <DetailStat label="Preço unitário" value={item.unit_price != null ? `${Number(item.unit_price).toFixed(2)} €` : "—"} />
              <DetailStat label="IVA" value={item.vat_rate != null ? `${Number(item.vat_rate)}%` : "—"} />
              <DetailStat label="Tamanho embalagem" value={item.pack_size != null ? `${Number(item.pack_size)} ${item.unit_label || ""}` : "—"} />
              <DetailStat label="Total c/ IVA" value={item.total != null ? `${Number(item.total).toFixed(2)} €` : "—"} />
            </div>

            {/* Consumption profile */}
            {(Number(item.consumption_light) > 0
              || Number(item.consumption_medium) > 0
              || Number(item.consumption_high) > 0) && (
              <div className="rounded-sm border border-[#1a4640] p-4">
                <div className="eyebrow mb-2">Consumo por limpeza ({item.consumption_period || "day"})</div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <ConsumeCell label="Leve"  value={item.consumption_light}  unit={item.unit_label} />
                  <ConsumeCell label="Médio" value={item.consumption_medium} unit={item.unit_label} />
                  <ConsumeCell label="Alto"  value={item.consumption_high}   unit={item.unit_label} />
                </div>
              </div>
            )}

            {/* Movement aggregates */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <DetailStat label="Entradas (fatura)" value={`+${round3(totals.invoice?.qty)} ${item.unit_label || ""}`}
                          hint={`${totals.invoice?.n || 0} movs.`} color="text-[#10b981]" />
              <DetailStat label="Consumo registado" value={`−${round3(totals.consumption?.qty)} ${item.unit_label || ""}`}
                          hint={`${totals.consumption?.n || 0} movs.`} color="text-[#ef4444]" />
              <DetailStat label="Estornos" value={round3(totals.invoice_reversal?.qty)}
                          hint={`${totals.invoice_reversal?.n || 0} movs.`} />
              <DetailStat label="Ajustes" value={round3(totals.adjust?.qty)}
                          hint={`${totals.adjust?.n || 0} movs.`} />
            </div>

            {/* Last invoice */}
            {data.last_invoice && (
              <div className="rounded-sm border border-[#1a4640] p-3 text-xs text-[#a0b2af]">
                Última fatura registada:{" "}
                <span className="text-[#d4af37] font-mono">{data.last_invoice.number}</span>
                {data.last_invoice.supplier && <> · {data.last_invoice.supplier}</>}
                {data.last_invoice.created_at && <> · {fmtDate(data.last_invoice.created_at)}</>}
              </div>
            )}

            {/* Movements timeline */}
            <div>
              <div className="eyebrow mb-2">Histórico (últimos {movements.length || 0})</div>
              {movements.length === 0 ? (
                <div className="text-xs italic text-[#a0b2af] text-center py-6 border border-dashed border-[#1a4640] rounded-sm">
                  Sem movimentos registados.
                </div>
              ) : (
                <ul className="divide-y divide-[#1a4640]/60 border border-[#1a4640] rounded-sm">
                  {movements.map(mv => (
                    <li key={mv.id} className="px-3 py-2 text-xs flex items-center gap-3"
                        data-testid={`stock-movement-${mv.id}`}>
                      <span className={`font-display text-lg shrink-0 ${kindColor(mv.kind)}`}>
                        {kindIcon(mv.kind)}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-[#fdfbf7]">
                          {kindLabel[mv.kind] || mv.kind}
                          <span className={`ml-2 font-mono tabular-nums ${kindColor(mv.kind)}`}>
                            {mv.kind === "consumption" || mv.kind === "out"
                              ? `−${round3(mv.quantity)}`
                              : `+${round3(mv.quantity)}`}
                            {" "}{item.unit_label || "un"}
                          </span>
                        </div>
                        <div className="text-[10px] text-[#a0b2af] truncate">
                          {fmtDate(mv.created_at)}
                          {mv.invoice_number && <> · Fatura <span className="font-mono text-[#d4af37]">{mv.invoice_number}</span></>}
                          {mv.booking_id && <> · Reserva <span className="font-mono">{mv.booking_id.slice(0, 8)}</span></>}
                          {mv.note && <> · {mv.note}</>}
                        </div>
                      </div>
                      <div className="text-right shrink-0 text-[10px] text-[#a0b2af]">
                        saldo {round3(mv.balance_after)}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DetailStat({ label, value, hint, color }) {
  return (
    <div className="rounded-sm border border-[#1a4640] bg-[#031c18] p-3">
      <div className="eyebrow text-[10px] text-[#a0b2af]">{label}</div>
      <div className={`font-display text-lg mt-0.5 tabular-nums ${color || "text-[#fdfbf7]"}`}>{value ?? "—"}</div>
      {hint && <div className="text-[10px] text-[#a0b2af] mt-0.5">{hint}</div>}
    </div>
  );
}

function ConsumeCell({ label, value, unit }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">{label}</div>
      <div className="font-display text-base text-[#fdfbf7] tabular-nums mt-0.5">
        {Math.round((Number(value) || 0) * 1000) / 1000}
        <span className="text-[10px] text-[#a0b2af] ml-1">{unit || "un"}</span>
      </div>
    </div>
  );
}

function round3(n) {
  return Math.round((Number(n) || 0) * 1000) / 1000;
}

